<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { z, type ZodFormattedError } from 'zod';
import { storeToRefs } from 'pinia';
import AppDialog from '../app-dialog.vue';
import AppButton from '@/core/components/common/app-button.vue';
import AppFormInput from '@/core/components/form/app-form-input.vue';
import AppFormInputTextarea from '@/core/components/form/app-form-input-textarea.vue';
import { useCoreStore } from '@/stores/core/store';
import { useProfilesStore } from '@/stores/profiles/store';
import { Profile } from '@/stores/profiles/models';
import useNotifications from '@/core/composable/use-notifications';
import { ToastNotificationAction } from '@/stores/core/models';
import { ROUTE_PROFILE_PROPERTIES } from '@/router/profiles/routes';

const store = useProfilesStore();
const coreStore = useCoreStore();
const { showSuccess, showError } = useNotifications();
const { isCreateProfileDialogVisible } = storeToRefs(store);
const { collapsibleTableContainerData } = storeToRefs(coreStore);

const vm = ref(new Profile());
const isSaving = ref(false);
const errors = ref<ZodFormattedError<Profile> | null>(null);
const schema = z.object({
  profileName: z.string().trim().min(1, { message: 'core.validation.required' }),
  description: z.string().trim().min(1, { message: 'core.validation.required' }),
});

const canSave = computed(() => schema.safeParse(vm.value).success && !isSaving.value);

const init = () => {
  vm.value = new Profile();
  isSaving.value = false;
  errors.value = null;
};

watch(
  () => isCreateProfileDialogVisible.value,
  (value) => {
    if (value) init();
  }
);

const onCancel = () => {
  isCreateProfileDialogVisible.value = false;
};

const onSave = async () => {
  const validation = schema.safeParse(vm.value);
  if (!validation.success) {
    errors.value = validation.error.format() as ZodFormattedError<Profile>;
    return;
  }

  errors.value = null;

  isSaving.value = true;
  try {
    const savedProfile = await store.saveProfile(vm.value);
    const savedId = Number(savedProfile?.id || 0);
    let action: ToastNotificationAction | undefined = undefined;
    if (savedId > 0) {
      action = new ToastNotificationAction();
      action.label = 'core.viewDetails';
      action.route = {
        name: ROUTE_PROFILE_PROPERTIES,
        params: { id: savedId },
      };
    }

    showSuccess('core.saveSuccessfullyDone', 'core.save', 1, action);
    isCreateProfileDialogVisible.value = false;
    collapsibleTableContainerData.value.refreshRequestCount++;
  } catch {
    showError('core.saveFailed', 'core.save');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <app-dialog
    :width="620"
    :is-visible="isCreateProfileDialogVisible"
    @update:is-visible="onCancel"
    content-title="profiles.new"
  >
    <div class="mt-4 flex flex-col gap-4">
      <app-form-input v-model="vm.profileName" label="core.name" :errors="errors?.profileName" />
      <app-form-input-textarea v-model="vm.description" label="core.description" :errors="errors?.description" />
    </div>

    <template #actions>
      <div class="flex gap-2 w-full">
        <div class="flex-1">
          <app-button
            icon="hugeicons:cancel-01"
            label="core.cancel2"
            outlined
            cssClass="w-full"
            severity="regular"
            @click="onCancel"
            :reverse-icon-direction="true"
          />
        </div>
        <div class="flex-1">
          <app-button
            icon="hugeicons:floppy-disk"
            :outlined="false"
            severity="primary"
            cssClass="w-full"
            label="core.save"
            :disabled="!canSave"
            @click="onSave"
          />
        </div>
      </div>
    </template>
  </app-dialog>
</template>
